import React from "react";
import {
    ClipboardList,
    AlertTriangle,
    CheckCircle,
    Clock,
    DollarSign,
    TrendingUp,
    FileText,
    ShieldCheck,
    Bell,
    ArrowRight
} from "lucide-react";

const SummaryCard = ({ title, value, sub, icon: Icon, color, trend }) => (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex justify-between items-start mb-4">
            <div className={`p-3 rounded-xl ${color}`}>
                <Icon size={24} />
            </div>
            {trend && (
                <span className="flex items-center gap-1 text-emerald-600 text-sm font-bold">
                    <TrendingUp size={14} /> {trend}
                </span>
            )}
        </div>
        <h3 className="text-slate-500 text-sm font-bold uppercase tracking-wider">{title}</h3>
        <div className="mt-1 flex items-baseline gap-2">
            <span className="text-3xl font-bold text-navy-900 font-serif">{value}</span>
            <span className="text-slate-400 text-sm">{sub}</span>
        </div>
    </div>
);

export default function CustomsDashboard() {
    const stats = [
        { title: "Draft Entries", value: "14", sub: "Entries", icon: FileText, color: "bg-blue-50 text-blue-600", trend: "+2" },
        { title: "Awaiting Docs", value: "8", sub: "Urgent", icon: Clock, color: "bg-amber-50 text-amber-600", trend: "+5" },
        { title: "DG Flagged", value: "3", sub: "Shipments", icon: AlertTriangle, color: "bg-red-50 text-red-600" },
        { title: "Estimated Duty", value: "$42.5k", sub: "MTD", icon: DollarSign, color: "bg-emerald-50 text-emerald-600", trend: "+12%" },
    ];

    return (
        <div className="max-w-7xl mx-auto py-8">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-navy-900 font-serif">Customs Dashboard</h1>
                <p className="text-slate-500 mt-1">Real-time overview of customs lodgements and compliance status.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                {stats.map((s, idx) => <SummaryCard key={idx} {...s} />)}
            </div>

            <div className="bg-amber-50 border border-amber-200 p-4 rounded-2xl flex gap-4 items-center mb-10 animate-in slide-in-from-top-4 duration-500">
                <div className="w-12 h-12 bg-amber-100 text-amber-700 rounded-xl flex items-center justify-center shrink-0 shadow-inner">
                    <Bell size={24} />
                </div>
                <div className="flex-1">
                    <h4 className="font-bold text-amber-900">ATT Action Required</h4>
                    <p className="text-sm text-amber-700">New ATT documents have been uploaded by <span className="font-bold">SME Global</span>. High-priority review suggested for upcoming ABF audit.</p>
                </div>
                <button className="bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-amber-800 transition-all flex items-center gap-2">
                    Review Docs <ArrowRight size={16} />
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Recent Entries */}
                <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                        <h3 className="font-bold text-navy-900 flex items-center gap-2">
                            <ClipboardList size={20} className="text-navy-900" /> Recent Entries
                        </h3>
                        <button className="text-navy-700 text-sm font-bold hover:underline">View All</button>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {[
                            { id: "TGL-2026-001", client: "SME Global", status: "Lodged", date: "Today, 10:45 AM" },
                            { id: "TGL-2026-002", client: "Nexus Freight", status: "Draft", date: "Today, 09:12 AM" },
                            { id: "TGL-2025-998", client: "EcoFlow Ltd", status: "Cleared", date: "Yesterday" }
                        ].map((entry) => (
                            <div key={entry.id} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-navy-50 text-navy-900 flex items-center justify-center font-bold text-xs">
                                        EP
                                    </div>
                                    <div>
                                        <div className="font-bold text-navy-900">{entry.id}</div>
                                        <div className="text-xs text-slate-400">{entry.client}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-6">
                                    <div className="text-right text-xs text-slate-400">{entry.date}</div>
                                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${entry.status === 'Cleared' ? 'bg-emerald-100 text-emerald-700' :
                                        entry.status === 'Lodged' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                                        }`}>
                                        {entry.status}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ABF Responses */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-slate-100 bg-slate-50/50">
                        <h3 className="font-bold text-navy-900 flex items-center gap-2">
                            <CheckCircle size={20} className="text-emerald-600" /> ABF Live Stream
                        </h3>
                    </div>
                    <div className="p-6 space-y-6">
                        <div className="flex gap-4">
                            <div className="w-1 h-12 bg-emerald-500 rounded-full shrink-0"></div>
                            <div>
                                <div className="text-sm font-bold text-navy-900">Entry Released</div>
                                <p className="text-xs text-slate-500">TGL-2025-998 cleared automated security check.</p>
                                <span className="text-[10px] text-slate-400">2 mins ago</span>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="w-1 h-12 bg-amber-500 rounded-full shrink-0"></div>
                            <div>
                                <div className="text-sm font-bold text-navy-900">Customs Query</div>
                                <p className="text-xs text-slate-500">Document inspection required for TGL-2026-004.</p>
                                <span className="text-[10px] text-slate-400">45 mins ago</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
